import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { AuthContext } from '../../../../context/auth/auth-context'
import axios from 'axios'
import {
  type AlertColor, Button, Modal, Stack, Typography, useTheme, Grid, Card, CardContent, Chip, IconButton, Box, Switch,
  FormControlLabel, Divider, Tooltip,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import PowerIcon from '@mui/icons-material/Power'
import PowerOffIcon from '@mui/icons-material/PowerOff'
import { formatToLocalMilliTimestampFromUTCNumber } from '../../../../utils/time-utils'
import { formAlertErrorMessage, formAlertSuccessMessage } from '../../../../utils/string-utils'
import MiniLoaderLabel from '../../../../components/loader/mini-loader-label'
import ToastWithMessage from '../../../../components/toast/toast-with-message'
import ViewJSONTrigger from '../view/view-json-trigger'

interface OrderManagerModalProps {
  open: boolean
  onClose: () => void
  strategyId: string
  title: string
  dataUrl: string | null
  postUrl: string | null
}

interface OrderManager {
  name: string
  states: {
    status: string
    tradable: boolean
    prefix: string
    'count.live': number
    'count.audit': number
    'count.exception': number
    'count.requests': number
    [key: string]: any
  }
  parameters: Record<string, string>
}

const OrderManagerModal: React.FC<OrderManagerModalProps> = (
  { open, onClose, strategyId, title, dataUrl, postUrl }
) => {
  const theme = useTheme()

  const [data, setData] = useState<Record<string, Record<string, OrderManager>>>({})
  const [retrievalTimestamp, setRetrievalTimestamp] = useState<number>(0)
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({})

  const { isAuthorized, accessToken } = useContext(AuthContext)

  const getOrderManagers = useCallback(() => {
    if (dataUrl != null && dataUrl.length !== 0) {
      axios
        .get(dataUrl, { headers: { Authorization: `Bearer ${accessToken}` } })
        .then(response => {
          const orderManagers: OrderManager[] = response.data.components
          const groupedOrderManagers: Record<string, Record<string, OrderManager>> = {}
          for (const orderManager of orderManagers) {
            const nameSplits = orderManager.name.split('.', 2)
            const venueId = nameSplits[0]
            if (groupedOrderManagers[venueId] == null) {
              groupedOrderManagers[venueId] = {}
            }

            const account = nameSplits[1]
            const internalMap = groupedOrderManagers[venueId]
            if (internalMap != null) {
              if (internalMap[account] != null) {
                console.error('Fatal error: duplicate account found')
                return
              }
              internalMap[account] = orderManager
            }
          }

          setData(groupedOrderManagers)
          setRetrievalTimestamp(Date.now())
        })
        .catch(reason => {
          console.error(reason)
          setData({})
          setRetrievalTimestamp(-1)
        })
    }
  }, [isAuthorized, accessToken, dataUrl])

  useEffect(() => {
    if (open && isAuthorized) {
      getOrderManagers()
    }
  }, [open, isAuthorized])

  const postRequest = useCallback((venueAccount: string, message: object) => {
    if (postUrl == null || postUrl.length === 0) {
      console.debug('[Order Manager Modal] Post url is empty')
      displayToastWithMessage('The POST url is invalid - please check the related configuration again', 'warning')
      return
    }

    console.debug(`[Order Manager Modal] Queuing update request for the account: ${venueAccount}`)
    console.debug(message)

    axios
      .post(postUrl, message, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(response => {
        console.debug(response)
        displayToastWithMessage(formAlertSuccessMessage(response.status, response.statusText, response.data.message), 'success')
      })
      .catch(error => {
        console.error(error)
        displayToastWithMessage(formAlertErrorMessage(error), 'error')
      })
      .finally(() => {
        getOrderManagers()
        setLoadingStates(prevState => { return { ...prevState, [venueAccount]: false } })
      })
  }, [strategyId, postUrl, accessToken])

  const updateTradable = useCallback((venueId: string, account: string, tradableFlag: boolean) => {
    const message = {
      timestamp: new Date().toISOString(),
      strategyId,
      command: tradableFlag ? `${venueId}.${account},connect` : `${venueId}.${account},disconnect`,
    }
    postRequest(`${venueId}.${account}`, message)
  }, [postRequest, postUrl, strategyId])

  const [toastOpen, setToastOpen] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastSeverity, setToastSeverity] = useState<AlertColor>('info')
  const displayToastWithMessage = useCallback((message: string, severity: AlertColor): void => {
    setToastMessage(message)
    setToastSeverity(severity)
    setToastOpen(true)
  }, [strategyId])

  const children = useMemo<React.ReactElement[]>(() => {
    return (
      Object.entries(data)
        .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
        .map(([venueId, mapValue]) => {
          const children = Object.entries(mapValue)
            .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
            .map(([account, orderManager]) => {
              const states = orderManager.states
              const venueAccount = `${venueId}.${account}`
              return (
                <Card key={venueAccount} variant='outlined'>
                  <CardContent>
                    <Stack gap={1} padding={1}>
                      <Stack direction='row' gap={1} alignItems='center'>
                        <Typography fontSize='large' fontWeight='bold'>{account}</Typography>
                        <Box marginX='auto'></Box>
                        <Chip
                          size='small'
                          variant='outlined'
                          color={states.tradable ? 'success' : 'error'}
                          label={states.tradable ? 'Tradable' : 'Untradable'}
                        />
                        <Tooltip title={states.status === 'OPEN' ? 'Open' : 'Closed'}>
                          {
                            states.status === 'OPEN' ? <PowerIcon color='success'/> : <PowerOffIcon color='error'/>
                          }
                        </Tooltip>
                      </Stack>

                      <Divider orientation='horizontal' flexItem><Typography>Orders</Typography></Divider>

                      <Grid container gap={1} justifyContent='center'>
                        <Grid item>
                          <Chip size='small' variant='outlined'
                                label={`Live: ${states['count.live']}`}
                                color={states['count.live'] > 0 ? 'success' : 'default'}
                          />
                        </Grid>
                        <Grid item>
                          <Chip size='small' variant='outlined'
                                label={`Audit: ${states['count.audit']}`}
                                color={states['count.audit'] > 0 ? 'warning' : 'default'}
                          />
                        </Grid>
                        <Grid item>
                          <Chip size='small' variant='outlined'
                                label={`Requests: ${states['count.requests']}`}
                                color={states['count.requests'] > 0 ? 'info' : 'default'}
                          />
                        </Grid>
                        <Grid item>
                          <Chip size='small' variant='outlined'
                                label={`Exception: ${states['count.exception']}`}
                                color={states['count.exception'] > 0 ? 'error' : 'default'}
                          />
                        </Grid>
                      </Grid>

                      <Divider orientation='horizontal' flexItem/>

                      <FormControlLabel
                        disableTypography
                        label={<MiniLoaderLabel label='Connected' loading={loadingStates[venueAccount]}/>}
                        control={
                          <Switch
                            checked={states.tradable}
                            onChange={(_event, checked) => {
                              setLoadingStates(prevState => { return { ...prevState, [venueAccount]: true } })
                              updateTradable(venueId, account, checked)
                            }}
                            inputProps={{ 'aria-label': 'controlled' }}
                            disabled={loadingStates[venueAccount]}
                          />
                        }
                      />
                    </Stack>
                  </CardContent>
                </Card>
              )
            })

          return (
            <Grid key={venueId} item xs={12} md={6} lg={3}>
              <Typography color='primary' fontWeight='bold' variant='h6' component='h2'>{venueId}</Typography>
              {children}
            </Grid>
          )
        })
    )
  }, [data, loadingStates])

  const onModalClose = useCallback(() => {
    setToastOpen(false)
    onClose()
  }, [onClose])

  return (
    <Modal
      open={open}
      onClose={onModalClose}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Stack
        sx={{
          position: 'absolute' as 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: '1600px',
          height: '80%',
          maxHeight: '1000px',
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.primary.main}`,
          boxShadow: 24,
          p: 4,
          color: 'white',
          overflowY: 'auto',
        }}
        gap={2}
      >
        <Stack direction='row' alignItems='center' gap={1}>
          <Typography id='modal-modal-title' fontWeight='bold' variant='h5' component='h1'>
            {title}
          </Typography>
          <Chip
            label={formatToLocalMilliTimestampFromUTCNumber(retrievalTimestamp)}
            size='small'
          />
          <ViewJSONTrigger dataUrl={dataUrl}/>
          <Box marginX='auto'></Box>
          <IconButton color='error' onClick={onModalClose}>
            <CloseIcon/>
          </IconButton>
        </Stack>

        <Button variant='outlined' sx={{ width: 'fit-content' }} startIcon={<RefreshIcon/>} onClick={getOrderManagers}>
          Reload
        </Button>

        <Grid container spacing={2}>
          {children}
        </Grid>

        <ToastWithMessage toastOpen={toastOpen} setToastOpen={setToastOpen} message={toastMessage} severity={toastSeverity}/>
      </Stack>
    </Modal>
  )
}

export default OrderManagerModal
